<template>
  <section class="ht-hero" :class="{ 'is-thai': $i18n.locale === 'th' }" id="section1">
    <div class="ht-hero__media" aria-hidden="true">
      <img
        class="ht-hero__image"
        :src="heroImage"
        alt=""
        width="1586"
        height="992"
        fetchpriority="high"
      />
    </div>

    <div class="ht-hero__inner container">
      <p class="ht-hero__eyebrow section-label" data-aos="fade-up" data-aos-delay="100">
        <span class="section-label__text">{{ $t('Home.section1.title1') }}</span>
      </p>

      <!-- Only the font of "thing" changes; the letters stay the same, so the heading reads normally. -->
      <h1 class="ht-hero__title">
        <span class="ht-hero__word">{{ $t('Home.section1.brand_hub') }}</span>
        <span class="ht-hero__thing" :class="{ 'is-waiting': thingFont === null }">
          <span class="ht-hero__thing-word" :class="thingClass">{{
            $t('Home.section1.brand_thing')
          }}</span>
        </span>
        <span class="ht-hero__word">{{ $t('Home.section1.brand_utopia') }}</span>
      </h1>

      <p class="ht-hero__lead" data-aos="fade-up" data-aos-delay="200">
        {{ $t('Home.section1.title2') }}
      </p>
      <p class="ht-hero__tagline" data-aos="fade-up" data-aos-delay="250">
        {{ $t('Home.section1.tagline') }}
      </p>

      <div class="ht-hero__actions" data-aos="fade-up" data-aos-delay="300">
        <router-link class="btn-default round ht-hero__btn ht-hero__btn--primary" to="/contact">
          <span>{{ $t('Home.contact_us_btn') }}</span>
          <Icon name="arrow-right" size="20" icon-class="icon" aria-hidden="true" />
        </router-link>

        <div class="ht-hero__package">
          <!-- Package disabled until the package page is ready -->
          <button
            type="button"
            class="btn-default round ht-hero__btn ht-hero__btn--package btn-disabled"
            disabled
          >
            {{ $t('Home.package_btn') }}
          </button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import Icon from '../icon/Icon'

// Loaded in public/index.html; the "thing" fonts are subset to its letters.
// Manrope is the title font — waiting for it keeps "Hub"/"Utopia" still during the cuts.
const FONTS = [
  '800 1em "Manrope"',
  'italic 800 1em "Playfair Display"',
  '1em "UnifrakturMaguntia"',
  '600 1em "Oswald"',
  '700 1em "Space Mono"',
  '1em "Pacifico"'
]

// Fast cuts that slow down (ease out), then settle on the script font.
// Holds count from the frame each cut is painted (+~1 frame each): ends ≈1.25s, settles ≈1.5s.
const SEQUENCE = [
  { font: 'serif', hold: 60 },
  { font: 'blackletter', hold: 60 },
  { font: 'condensed', hold: 65 },
  { font: 'mono', hold: 70 },
  { font: 'serif', hold: 75 },
  { font: 'blackletter', hold: 85 },
  { font: 'condensed', hold: 105 },
  { font: 'mono', hold: 135 },
  { font: 'serif', hold: 180 },
  { font: 'blackletter', hold: 240 }
]
const FINAL_FONT = 'script'
const FONT_WAIT_LIMIT = 1500

export default {
  name: 'HeroUtopia',
  components: { Icon },
  data() {
    return {
      // null = waiting for fonts (the slot keeps its width but the word is hidden)
      thingFont: null,
      settling: false
    }
  },
  computed: {
    heroImage() {
      return this.$store.state.userLogin
        ? require('@/assets/images/Newlandindpage/hero/hero-background-light.png')
        : require('@/assets/images/Newlandindpage/hero/hero-background.png')
    },
    thingClass() {
      return {
        [`is-${this.thingFont}`]: this.thingFont !== null,
        'is-settling': this.settling
      }
    }
  },
  mounted() {
    this.timers = []
    this.frameId = null
    this.isDestroyed = false

    if (this.prefersReducedMotion()) {
      this.thingFont = FINAL_FONT
      return
    }

    this.waitForFonts().then(() => {
      if (!this.isDestroyed) this.playSequence()
    })
  },
  beforeDestroy() {
    this.isDestroyed = true
    this.timers.forEach(id => clearTimeout(id))
    this.timers = []
    if (this.frameId) cancelAnimationFrame(this.frameId)
  },
  methods: {
    prefersReducedMotion() {
      return (
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches
      )
    },
    later(fn, ms) {
      return new Promise(resolve => {
        this.timers.push(
          setTimeout(() => {
            if (fn) fn()
            resolve()
          }, ms)
        )
      })
    },
    waitForFonts() {
      if (!document.fonts || typeof document.fonts.load !== 'function') return Promise.resolve()
      const text = this.$t('Home.section1.brand_thing')
      const loads = FONTS.map(font => document.fonts.load(font, text).catch(() => []))
      // Never hold the title back for long if a font is slow or blocked.
      return Promise.race([Promise.all(loads), this.later(null, FONT_WAIT_LIMIT)])
    },
    // Driven by animation frames (not a batch of timeouts) so every cut is painted
    // and held, even if the main thread is busy while the page loads.
    playSequence() {
      let index = 0
      let shownAt = null
      this.thingFont = SEQUENCE[0].font
      const tick = now => {
        this.frameId = null
        if (this.isDestroyed) return
        // First frame after a cut = the cut is on screen; start its hold here.
        if (shownAt === null) shownAt = now
        else if (now - shownAt >= SEQUENCE[index].hold) {
          index += 1
          shownAt = null
          if (index >= SEQUENCE.length) {
            this.thingFont = FINAL_FONT
            this.settling = true
            return
          }
          this.thingFont = SEQUENCE[index].font
        }
        this.frameId = requestAnimationFrame(tick)
      }
      this.frameId = requestAnimationFrame(tick)
    }
  }
}
</script>
